import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  MapPin, 
  Calendar, 
  Users, 
  DollarSign,
  Mail,
  Clock,
  Activity,
  Plus,
  Download,
  BarChart,
  AlertCircle
} from 'lucide-react';
import { api } from '../utils/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    venues: 0,
    events: 0,
    subscribers: 0,
    revenue: 0,
    expenses: 0,
    profit: 0
  });
  const [activities, setActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [statsResponse, activitiesResponse, eventsResponse, financialResponse] = await Promise.all([
        api.stats.get(),
        api.activities.get(),
        api.events.getAll(),
        api.reports.financial()
      ]);

      // Merge financial data into stats
      setStats({
        ...statsResponse.stats,
        revenue: financialResponse.report?.summary?.totalRevenue || 0,
        expenses: financialResponse.report?.summary?.totalExpenses || 0,
        profit: financialResponse.report?.summary?.netProfit || 0
      });
      setActivities(activitiesResponse.activities);
      
      // Filter upcoming events
      const upcoming = eventsResponse.events
        .filter(event => event.status !== 'completed' && event.status !== 'cancelled')
        .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0))
        .slice(0, 3);
      
      setUpcomingEvents(upcoming);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatActivity = (activity) => {
    const actionMap = {
      'VIEW_VENUES': 'Viewed venues',
      'CREATE_VENUE': 'Added new venue',
      'UPDATE_VENUE': 'Updated venue',
      'DELETE_VENUE': 'Deleted venue',
      'VIEW_EVENTS': 'Viewed events',
      'CREATE_EVENT': 'Created event',
      'UPDATE_EVENT': 'Updated event',
      'DELETE_EVENT': 'Deleted event',
      'VIEW_CONTACTS': 'Viewed contacts',
      'CREATE_CONTACT': 'Added new contact',
      'UPDATE_CONTACT': 'Updated contact',
      'DELETE_CONTACT': 'Deleted contact',
      'IMPORT_CONTACTS': 'Imported contacts',
      'VIEW_SUBSCRIBERS': 'Viewed subscribers',
      'CREATE_SUBSCRIBER': 'Added subscriber',
      'UPDATE_SUBSCRIBER': 'Updated subscriber',
      'DELETE_SUBSCRIBER': 'Deleted subscriber',
      'VIEW_STATS': 'Viewed dashboard',
      'UPDATE_SETTINGS': 'Updated settings',
      'VIEW_TRANSACTIONS': 'Viewed transactions',
      'CREATE_TRANSACTION': 'Added transaction',
      'VIEW_FINANCIAL_REPORT': 'Viewed financial report'
    };

    return {
      action: actionMap[activity.action] || activity.action,
      details: activity.details ? 
        (activity.details.name || 
         activity.details.email || 
         (activity.details.amount && `€${activity.details.amount} - ${activity.details.category}`) ||
         `${activity.details.count} items`) : 
        '',
      time: getRelativeTime(activity.timestamp)
    };
  };

  const getRelativeTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const statsData = [
    { 
      label: 'Total Venues', 
      value: stats.venues.toString(), 
      icon: MapPin, 
      change: 'Active locations', 
      color: 'text-[#2D6B42]',
      route: '/venues'
    },
    { 
      label: 'Upcoming Events', 
      value: stats.events.toString(), 
      icon: Calendar, 
      change: 'This season', 
      color: 'text-[#D4A574]',
      route: '/events'
    },
    { 
      label: 'Mailing List', 
      value: stats.subscribers.toString(), 
      icon: Mail, 
      change: 'Active subscribers', 
      color: 'text-blue-500',
      route: '/mailing-list'
    },
    { 
      label: 'Net Profit', 
      value: stats.profit >= 0 ? `€${stats.profit.toLocaleString()}` : `-€${Math.abs(stats.profit).toLocaleString()}`, 
      icon: DollarSign, 
      change: stats.profit >= 0 ? `+${((stats.profit / (stats.revenue || 1)) * 100).toFixed(0)}% margin` : 'Loss', 
      color: stats.profit >= 0 ? 'text-green-500' : 'text-red-500',
      route: '/financials'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-light mb-2">Dashboard</h1>
        <p className="text-[#888888]">Welcome back. Here's your overview.</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsData.map((stat, index) => (
          <div 
            key={index} 
            className="card-dark group hover:border-[#2D6B42] transition-all duration-300 cursor-pointer"
            onClick={() => navigate(stat.route)}
          >
            <div className="flex items-start justify-between mb-4">
              <stat.icon className={`${stat.color} opacity-80`} size={24} />
              <span className="text-xs text-[#888888]">{stat.change}</span>
            </div>
            <p className="text-3xl font-light mb-1">{stat.value}</p>
            <p className="text-[#888888] text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="card-dark">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-light">Recent Activity</h2>
            <Activity className="text-[#888888]" size={20} />
          </div>
          <div className="space-y-4">
            {activities.length > 0 ? (
              activities.slice(0, 5).map((activity, index) => {
                const formatted = formatActivity(activity);
                return (
                  <div key={index} className="flex items-start gap-4 pb-4 border-b border-[#333333] last:border-0">
                    <div className="w-2 h-2 bg-[#D4A574] rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{formatted.action}</p>
                      {formatted.details && (
                        <p className="text-xs text-[#888888]">{formatted.details}</p>
                      )}
                    </div>
                    <span className="text-xs text-[#888888] flex items-center gap-1">
                      <Clock size={12} />
                      {formatted.time}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-[#888888] text-center py-8">No recent activity</p>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="card-dark">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-light">Upcoming Events</h2>
            <Calendar className="text-[#888888]" size={20} />
          </div>
          <div className="space-y-4">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-[#0A0A0A] rounded-lg border border-[#333333] cursor-pointer hover:border-[#2D6B42] transition-colors"
                  onClick={() => navigate('/events')}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium">{event.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      event.status === 'planning' 
                        ? 'bg-[#2D6B42]/20 text-[#2D6B42]'
                        : event.status === 'confirmed'
                        ? 'bg-green-500/20 text-green-500'
                        : event.status === 'concept'
                        ? 'bg-[#888888]/20 text-[#888888]'
                        : 'bg-blue-500/20 text-blue-500'
                    }`}>
                      {event.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#888888]">
                    {event.date ? new Date(event.date).toLocaleDateString('en-GB', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'Date TBD'}
                  </p>
                  <p className="text-sm text-[#888888]">{event.venue || 'Venue TBD'}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-[#888888] mb-4">No upcoming events</p>
                <button 
                  onClick={() => navigate('/events')}
                  className="btn-secondary text-sm"
                >
                  Create First Event
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card-dark">
        <h2 className="text-xl font-light mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            onClick={() => navigate('/venues')}
            className="btn-secondary text-sm flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Add Venue
          </button>
          <button 
            onClick={() => navigate('/events')}
            className="btn-secondary text-sm flex items-center justify-center gap-2"
          >
            <Calendar size={16} />
            Create Event
          </button>
          <button 
            onClick={() => navigate('/contacts')}
            className="btn-secondary text-sm flex items-center justify-center gap-2"
          >
            <Download size={16} />
            Export Contacts
          </button>
          <button 
            onClick={() => navigate('/financials')}
            className="btn-secondary text-sm flex items-center justify-center gap-2"
          >
            <BarChart size={16} />
            View Analytics
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;